import React from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';

const GraficoEvolucaoConsolidado = ({ dadosEvolucao = [], empresas = [] }) => {
  // Obter cores das empresas da base de dados
  const coresEmpresas = empresas.reduce((acc, empresa) => {
    acc[empresa.nome] = empresa.cor;
    return acc;
  }, {});
  
  // Cor padrão para empresas não encontradas
  const COR_DEFAULT = '#6B7280';

  // Função para formatar o mês
  const formatarMes = (mes) => {
    const meses = {
      '01': 'Jan', '02': 'Fev', '03': 'Mar', '04': 'Abr',
      '05': 'Mai', '06': 'Jun', '07': 'Jul', '08': 'Ago',
      '09': 'Set', '10': 'Out', '11': 'Nov', '12': 'Dez'
    };
    
    if (meses[mes]) return meses[mes];
    if (typeof mes === 'string' && mes.includes('-')) {
      const mesNumero = mes.split('-')[1];
      return meses[mesNumero] || mes;
    }
    if (typeof mes === 'number') {
      return meses[mes.toString().padStart(2, '0')] || mes.toString();
    }
    return mes;
  };

  // Processar dados consolidados por empresa
  const processarDadosConsolidados = () => {
    const dadosPorMes = {};
    
    // Agrupar dados por mês
    dadosEvolucao.forEach((item) => {
      const mesFormatado = formatarMes(item.mes);
      
      if (!dadosPorMes[mesFormatado]) {
        dadosPorMes[mesFormatado] = {
          mes: mesFormatado,
          total: 0
        };
      }
      
      // Se tiver dados por empresa (modo consolidado)
      if (item.empresas && typeof item.empresas === 'object') {
        Object.keys(item.empresas).forEach(empresa => {
          const valorEmpresa = item.empresas[empresa];
          // Se for um objeto, pegar total_contratos, senão usar o valor direto
          const vendas = typeof valorEmpresa === 'object' ? valorEmpresa.total_contratos || 0 : valorEmpresa || 0;
          dadosPorMes[mesFormatado][empresa] = vendas;
          dadosPorMes[mesFormatado].total += vendas;
        });
      } else if (item.empresa) {
        // Dados individuais com empresa específica
        dadosPorMes[mesFormatado][item.empresa] = item.vendas || 0;
        dadosPorMes[mesFormatado].total += item.vendas || 0;
      } else {
        // Se não há empresa específica, usar o total geral
        dadosPorMes[mesFormatado].total += item.vendas || 0;
      }
    });

    return Object.values(dadosPorMes).sort((a, b) => {
      const ordemMeses = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];
      return ordemMeses.indexOf(a.mes) - ordemMeses.indexOf(b.mes);
    });
  };

  const dadosProcessados = processarDadosConsolidados();

  // Obter lista de empresas únicas
  const empresasUnicas = [...new Set(empresas.map(emp => emp?.name || emp?.nome).filter(Boolean))];

  // Se não há dados, mostrar mensagem de carregamento
  if (!dadosProcessados || dadosProcessados.length === 0) {
    return (
      <div className="bg-[color:var(--surface)] rounded-lg shadow-sm border border-[color:var(--border)] p-6">
        <div className="flex items-center justify-center h-80">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[color:var(--accent)] mx-auto mb-4"></div>
            <p className="text-[color:var(--text-muted)]">Carregando dados consolidados...</p>
          </div>
        </div>
      </div>
    );
  }


  
  return (
    <div className="bg-[color:var(--surface)] rounded-lg shadow-md p-6">
      <div className="mb-4">
        <h3 className="text-lg font-semibold text-[color:var(--text-primary)] mb-2">
          Evolução de Vendas Consolidada
        </h3>
        <p className="text-sm text-[color:var(--text-muted)]">
          Comparativo entre {empresasUnicas.join(' e ')}
        </p>
      </div>
      
      <div className="h-80">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={dadosProcessados}
            margin={{
              top: 20,
              right: 30,
              left: 20,
              bottom: 5,
            }}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis 
              dataKey="mes" 
              tick={{ fontSize: 12 }}
              axisLine={{ stroke: '#e5e7eb' }}
              tickLine={{ stroke: '#e5e7eb' }}
            />
            <YAxis 
              tick={{ fontSize: 12 }}
              axisLine={{ stroke: '#e5e7eb' }}
              tickLine={{ stroke: '#e5e7eb' }}
            />
            <Tooltip 
              contentStyle={{
                backgroundColor: '#f9fafb',
                border: '1px solid #e5e7eb',
                borderRadius: '8px',
                fontSize: '14px'
              }}
            />
            <Legend />
            
            {/* Barra para total consolidado */}
            <Bar 
              dataKey="total"
              fill="#6B7280"
              name="Total Consolidado"
              radius={[4, 4, 0, 0]}
            />
            
            {/* Barras para cada empresa */}
            {empresasUnicas.map((empresa) => (
              <Bar 
                key={empresa}
                dataKey={empresa}
                fill={coresEmpresas[empresa] || COR_DEFAULT}
                name={empresa}
                radius={[0, 0, 4, 4]}
              />
            ))}
          </BarChart>
        </ResponsiveContainer>
      </div>
      
      {/* Resumo dos dados */}
      <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-[color:var(--surface-muted)] p-3 rounded-lg">
          <p className="text-sm text-[color:var(--accent)] font-medium">Total de Vendas</p>
          <p className="text-lg font-bold text-[color:var(--accent)]">
            {dadosProcessados.reduce((total, item) => total + item.total, 0).toLocaleString()}
          </p>
        </div>
        <div className="bg-[color:var(--surface-muted)] p-3 rounded-lg">
          <p className="text-sm text-[color:var(--status-good)] font-medium">Empresas</p>
          <p className="text-lg font-bold text-[color:var(--status-good)]">
            {empresasUnicas.length}
          </p>
        </div>
        <div className="bg-[color:var(--surface-muted)] p-3 rounded-lg">
          <p className="text-sm text-[color:var(--accent)] font-medium">Período</p>
          <p className="text-lg font-bold text-[color:var(--accent)]">
            {dadosProcessados.length} meses
          </p>
        </div>
      </div>
    </div>
  );
};

export default GraficoEvolucaoConsolidado;




